/**
 * PublicFacilityPage
 *
 * Phase 5 — non-member booking surface for facilities. Mounted at
 * /:clubid/book.
 *
 * Three views in sequence:
 *   1. Gallery   — list of public resources + services. Click one to
 *                  start booking.
 *   2. Booking   — date + slot picker, contact info, email lookup +
 *                  optional password verify (matched-member path —
 *                  saved cards land in PublicPaymentStep on confirm).
 *   3. Payment   — PublicPaymentStep handles both PaymentIntent (auto-
 *                  confirm) and SetupIntent (staff_review) modes.
 *   4. Success   — confirmation screen.
 *
 * The flow mirrors PublicCampPage so visitors who've registered for a
 * camp recognize the rhythm. Email lookup hits the existing
 * /api/public/camps/lookup-member endpoint (same helpers, no
 * duplication on the backend).
 */

import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "react-toastify";
import { useTheme } from "../../../context/ThemeContext";
import {
  getPageBackground,
  getCardBackground,
  getPrimaryText,
  getSecondaryText,
  getMutedText,
  getInputStyles,
  getLightBorder,
} from "../../../utils/themeUtils";
import {
  listPublicResources,
  listPublicServices,
  getPublicResourceAvailability,
  getPublicServiceAvailability,
  getPublicResourceRequirements,
  getPublicServiceRequirements,
  createPublicBooking,
  lookupMemberByEmail,
  verifyMemberByEmail,
  PublicResource,
  PublicService,
  PublicAvailabilityResponse,
  PublicBookingRequirementsResponse,
} from "../../../api/publicFacilityAPI";
import PublicPaymentStep from "../../common/PublicPaymentStep";

const todayYmd = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const fmtTime = (iso: string) =>
  new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
const fmtDate = (ymd: string) =>
  new Date(`${ymd}T12:00:00`).toLocaleDateString(undefined, { weekday: "long", year: "numeric", month: "long", day: "numeric" });

const PublicFacilityPage: React.FC = () => {
  const { isDarkMode } = useTheme();
  const { clubid } = useParams<{ clubid: string }>();
  const tenantId = clubid || "";

  const [view, setView] = useState<"gallery" | "details" | "booking" | "payment" | "success">("gallery");

  // Gallery state
  const [resources, setResources] = useState<PublicResource[]>([]);
  const [services, setServices] = useState<PublicService[]>([]);
  const [loadingGallery, setLoadingGallery] = useState(true);

  // Booking state
  const [pick, setPick] = useState<{ kind: "resource"; data: PublicResource } | { kind: "service"; data: PublicService } | null>(null);
  const [date, setDate] = useState(todayYmd());
  const [availability, setAvailability] = useState<PublicAvailabilityResponse | null>(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<{ start_at: string; end_at: string } | null>(null);
  const [partySize, setPartySize] = useState(1);
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [notes, setNotes] = useState("");

  // Member email lookup state (mirrors PublicCampPage)
  const [, setEmailLookupRan] = useState(false);
  const [memberFound, setMemberFound] = useState(false);
  const [membershipId, setMembershipId] = useState<string | null>(null);
  // The server's proof that this visitor verified as that member with their
  // password. A booking that names a member is refused without it.
  const [memberToken, setMemberToken] = useState<string | null>(null);
  const [passwordPromptOpen, setPasswordPromptOpen] = useState(false);
  const [verifyPassword, setVerifyPassword] = useState("");
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [verifyLoading, setVerifyLoading] = useState(false);

  // Payment state
  const [submitting, setSubmitting] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [intentMode, setIntentMode] = useState<"payment" | "setup">("payment");
  const [requiresApproval, setRequiresApproval] = useState(false);
  const [bookingResult, setBookingResult] = useState<any>(null);
  const [nextSlotsAfterConflict, setNextSlotsAfterConflict] = useState<Array<{ start_at: string; end_at: string }>>([]);
  const [requiredWaivers, setRequiredWaivers] = useState<PublicBookingRequirementsResponse["required_waivers"]>([]);
  const [acceptedWaivers, setAcceptedWaivers] = useState<Set<string>>(new Set());

  // Load gallery on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [r, s] = await Promise.all([listPublicResources(tenantId), listPublicServices(tenantId)]);
      if (cancelled) return;
      setLoadingGallery(false);
      if (r.success) setResources(r.data || []);
      if (s.success) setServices(s.data || []);
    })();
    return () => { cancelled = true; };
  }, [tenantId]);

  // Load required waivers when pick changes.
  useEffect(() => {
    if (!pick) { setRequiredWaivers([]); setAcceptedWaivers(new Set()); return; }
    let cancelled = false;
    (async () => {
      const res = pick.kind === "service"
        ? await getPublicServiceRequirements(tenantId, pick.data.id)
        : await getPublicResourceRequirements(tenantId, pick.data.id);
      if (cancelled) return;
      if (res.success) {
        setRequiredWaivers(res.data.required_waivers || []);
        setAcceptedWaivers(new Set());
      } else {
        setRequiredWaivers([]);
      }
    })();
    return () => { cancelled = true; };
  }, [tenantId, pick]);

  // Load availability when pick or date changes.
  useEffect(() => {
    if (!pick || !date) { setAvailability(null); return; }
    let cancelled = false;
    setLoadingSlots(true);
    setSelectedSlot(null);
    setNextSlotsAfterConflict([]);
    (async () => {
      const res = pick.kind === "service"
        ? await getPublicServiceAvailability(tenantId, pick.data.id, date)
        : await getPublicResourceAvailability(tenantId, pick.data.id, date);
      if (cancelled) return;
      setLoadingSlots(false);
      if (res.success) setAvailability(res.data);
      else { toast.error((res as any).error || "Failed to load availability"); setAvailability(null); }
    })();
    return () => { cancelled = true; };
  }, [tenantId, pick, date]);

  // Email lookup on blur.
  const onEmailBlur = async () => {
    const email = contactEmail.trim();
    if (!email) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;
    setEmailLookupRan(true);
    try {
      const result = await lookupMemberByEmail(tenantId, email);
      if (result?.found && result.requires_password) {
        setPasswordPromptOpen(true);
      } else {
        setPasswordPromptOpen(false);
        setMembershipId(null);
        setMemberToken(null);
        setMemberFound(false);
      }
    } catch { /* non-blocking */ }
  };

  const onVerifyPassword = async () => {
    const email = contactEmail.trim();
    if (!email || !verifyPassword) {
      setVerifyError("Enter your password to load your account.");
      return;
    }
    setVerifyLoading(true);
    setVerifyError(null);
    try {
      const result = await verifyMemberByEmail(tenantId, email, verifyPassword);
      if (result?.found && result?.data?.membership_id) {
        setMembershipId(result.data.membership_id);
        setMemberToken(result.data.member_token || null);
        setMemberFound(true);
        if (result.data.contact_name && !contactName) setContactName(result.data.contact_name);
        if (result.data.contact_phone && !contactPhone) setContactPhone(result.data.contact_phone);
        setPasswordPromptOpen(false);
        setVerifyPassword("");
        toast.success("Account verified — saved cards will appear on the next step.");
      }
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 401) setVerifyError("Incorrect password. Try again or skip to register as a new visitor.");
      else if (status === 429) setVerifyError("Too many attempts. Please try again in a few minutes.");
      else setVerifyError("Couldn't verify your account.");
    } finally {
      setVerifyLoading(false);
    }
  };

  const skipPasswordPrompt = () => {
    setPasswordPromptOpen(false);
    setVerifyPassword("");
    setVerifyError(null);
  };

  const allWaiversAccepted = requiredWaivers.every((w) => acceptedWaivers.has(w.id));

  // Submit handler — picks resource_id vs service_id, sends to public bookings.
  const handleBook = async () => {
    if (!pick || !selectedSlot) return;
    if (!contactEmail) { toast.error("Email is required"); return; }
    // Block if password prompt is open and unverified — same guard as
    // public camps so visitors can't bypass it by submitting hot.
    if (passwordPromptOpen && !memberFound) {
      toast.error("Enter your password above to continue, or skip to register as a new visitor.");
      return;
    }
    if (!allWaiversAccepted) {
      toast.error("Please accept all required waivers before booking.");
      return;
    }
    setSubmitting(true);
    const res = await createPublicBooking(tenantId, {
      resource_id: pick.kind === "resource" ? pick.data.id : undefined,
      service_id: pick.kind === "service" ? pick.data.id : undefined,
      start_at: selectedSlot.start_at,
      end_at: selectedSlot.end_at,
      party_size: partySize,
      contact_email: contactEmail.trim(),
      contact_name: contactName || undefined,
      contact_phone: contactPhone || undefined,
      membership_id: membershipId || undefined,
      member_token: (membershipId && memberToken) || undefined,
      notes: notes || undefined,
      waiver_acknowledgments: requiredWaivers.length > 0 ? Array.from(acceptedWaivers) : undefined,
    });
    setSubmitting(false);
    if (res.success) {
      const r = res as any;
      setBookingResult(r);
      const cs = r.clientSecret;
      setIntentMode((r.intent_mode === "setup" ? "setup" : "payment") as "payment" | "setup");
      setRequiresApproval(!!r.requires_approval);
      if (cs) {
        setClientSecret(cs);
        setView("payment");
      } else {
        setView("success");
      }
      return;
    }
    const conflict = res as any;
    if (conflict.next_slots && conflict.next_slots.length > 0) {
      setNextSlotsAfterConflict(conflict.next_slots);
      setSelectedSlot(null);
      const refresh = pick.kind === "service"
        ? await getPublicServiceAvailability(tenantId, pick.data.id, date)
        : await getPublicResourceAvailability(tenantId, pick.data.id, date);
      if (refresh.success) setAvailability(refresh.data);
    }
    // ACCOUNT_EXISTS_SIGN_IN: the email belongs to an account and the visitor
    // did not enter its password. The message says to sign in or use a
    // different email; the password prompt is opened to do the first.
    if (conflict.requires_password_verification || conflict.code === "ACCOUNT_EXISTS_SIGN_IN") {
      setPasswordPromptOpen(true);
    }
    toast.error(conflict.error || "Booking failed");
  };

  const pickLabel = useMemo(
    () => pick ? (pick.kind === "service" ? pick.data.name : pick.data.name) : "",
    [pick]
  );

  // ============================
  // SUCCESS VIEW
  // ============================
  if (view === "success") {
    return (
      <div className={`min-h-screen ${getPageBackground(isDarkMode)} p-4 sm:p-8`}>
        <div className={`max-w-2xl mx-auto rounded-lg border p-6 text-center ${getLightBorder(isDarkMode)} ${getCardBackground(isDarkMode)}`}>
          <h1 className={`text-2xl font-bold mb-3 ${getPrimaryText(isDarkMode)}`}>
            {requiresApproval ? "Request submitted" : "Booking confirmed"}
          </h1>
          {requiresApproval && (
            <p className={`mb-3 text-sm ${getSecondaryText(isDarkMode)}`}>
              Staff will review your request and follow up via email.
              {bookingResult?.summary?.total > 0 && " Your card was authorized but won't be charged until approved."}
            </p>
          )}
          <p className={`mb-2 ${getSecondaryText(isDarkMode)}`}>{pickLabel}</p>
          {selectedSlot && (
            <p className={`mb-6 ${getSecondaryText(isDarkMode)}`}>
              {fmtDate(date)} · {fmtTime(selectedSlot.start_at)} – {fmtTime(selectedSlot.end_at)}
            </p>
          )}
          <Link to={`/${clubid}/book`} className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700">
            Back to facilities
          </Link>
        </div>
      </div>
    );
  }

  // ============================
  // PAYMENT VIEW
  // ============================
  if (view === "payment" && clientSecret) {
    const total = bookingResult?.summary?.total || 0;
    return (
      <div className={`min-h-screen ${getPageBackground(isDarkMode)} p-4 sm:p-8`}>
        <div className="max-w-2xl mx-auto">
          <h1 className={`text-2xl font-bold mb-4 ${getPrimaryText(isDarkMode)}`}>Complete payment</h1>
          <div className={`mb-3 ${getSecondaryText(isDarkMode)}`}>
            {pickLabel} · {fmtDate(date)} · {selectedSlot && `${fmtTime(selectedSlot.start_at)} – ${fmtTime(selectedSlot.end_at)}`}
          </div>
          <PublicPaymentStep
            clientSecret={clientSecret}
            intentMode={intentMode}
            totalAmt={total}
            participantCount={partySize}
            billingName={contactName || undefined}
            billingEmail={contactEmail || undefined}
            onComplete={() => setView("success")}
          />
        </div>
      </div>
    );
  }

  // ============================
  // DETAILS VIEW (gallery + description; "Book this" advances to booking)
  // ============================
  if (view === "details" && pick) {
    const photos = pick.data.image_urls || [];
    const description = pick.data.description;
    return (
      <div className={`min-h-screen ${getPageBackground(isDarkMode)} p-4 sm:p-8`}>
        <div className="max-w-3xl mx-auto">
          <button onClick={() => { setPick(null); setView("gallery"); }}
            className={`text-sm mb-3 ${getMutedText(isDarkMode)} hover:underline`}>
            ← Back to facilities
          </button>
          <h1 className={`text-2xl font-bold mb-2 ${getPrimaryText(isDarkMode)}`}>{pick.data.name}</h1>
          {pick.kind === "resource" && (
            <p className={`text-sm mb-4 ${getMutedText(isDarkMode)}`}>
              {pick.data.facility_resource_types?.display_name}
              {pick.data.capacity > 1 && ` · capacity ${pick.data.capacity}`}
            </p>
          )}
          {pick.kind === "service" && (
            <p className={`text-sm mb-4 ${getMutedText(isDarkMode)}`}>
              Package · {pick.data.duration_minutes} minutes
            </p>
          )}
          {photos.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
              {photos.map((p, i) => (
                <img key={i} src={p.url} alt={p.alt || pick.data.name}
                  className="w-full h-40 object-cover rounded" loading="lazy" />
              ))}
            </div>
          )}
          {description && (
            <p className={`text-sm mb-6 whitespace-pre-line ${getSecondaryText(isDarkMode)}`}>{description}</p>
          )}
          <button onClick={() => setView("booking")}
            className="px-4 py-2 rounded bg-blue-600 text-white text-sm hover:bg-blue-700">
            Book this
          </button>
        </div>
      </div>
    );
  }

  // ============================
  // BOOKING VIEW (date + slots + contact info)
  // ============================
  if (view === "booking" && pick) {
    return (
      <div className={`min-h-screen ${getPageBackground(isDarkMode)} p-4 sm:p-8`}>
        <div className="max-w-3xl mx-auto">
          <button onClick={() => { setPick(null); setView("gallery"); }} className={`text-sm mb-3 ${getMutedText(isDarkMode)} hover:underline`}>
            ← Back to facilities
          </button>
          <h1 className={`text-2xl font-bold mb-2 ${getPrimaryText(isDarkMode)}`}>{pickLabel}</h1>
          {pick.kind === "service" && pick.data.description && (
            <p className={`mb-3 text-sm ${getSecondaryText(isDarkMode)}`}>{pick.data.description}</p>
          )}
          {pick.kind === "resource" && pick.data.description && (
            <p className={`mb-3 text-sm ${getSecondaryText(isDarkMode)}`}>{pick.data.description}</p>
          )}

          <div className={`rounded-lg border p-4 mb-4 ${getLightBorder(isDarkMode)} ${getCardBackground(isDarkMode)}`}>
            <label className={`block text-xs ${getMutedText(isDarkMode)}`}>
              Date
              <input
                type="date" autoComplete="off"
                value={date}
                min={todayYmd()}
                onChange={(e) => setDate(e.target.value)}
                className={`w-full mt-1 ${getInputStyles(isDarkMode)} px-2 py-2 rounded`}
              />
            </label>
          </div>

          <div className={`rounded-lg border p-4 mb-4 ${getLightBorder(isDarkMode)} ${getCardBackground(isDarkMode)}`}>
            <h2 className={`text-lg font-semibold mb-3 ${getPrimaryText(isDarkMode)}`}>{fmtDate(date)}</h2>
            {loadingSlots ? (
              <p className={`text-sm ${getMutedText(isDarkMode)}`}>Loading…</p>
            ) : !availability || availability.slots.length === 0 ? (
              <p className={`text-sm ${getMutedText(isDarkMode)}`}>No times available on this date. Try a different day.</p>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                {availability.slots.map((s) => {
                  const isSelected = selectedSlot && selectedSlot.start_at === s.start_at;
                  return (
                    <button
                      key={s.start_at}
                      type="button"
                      onClick={() => setSelectedSlot(s)}
                      className={`px-2 py-2 rounded text-sm border ${
                        isSelected ? "bg-blue-600 text-white border-blue-600"
                          : isDarkMode ? "border-gray-600 text-gray-100 hover:bg-gray-700"
                          : "border-gray-300 text-gray-800 hover:bg-gray-100"
                      }`}
                    >
                      {fmtTime(s.start_at)}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {nextSlotsAfterConflict.length > 0 && (
            <div className={`rounded-lg border p-3 mb-4 bg-amber-50 dark:bg-amber-900/20 ${getLightBorder(isDarkMode)}`}>
              <p className={`text-sm font-medium mb-2 ${getPrimaryText(isDarkMode)}`}>Your slot was just taken. Try one of these instead:</p>
              <div className="flex flex-wrap gap-2">
                {nextSlotsAfterConflict.map((s) => (
                  <button
                    key={s.start_at}
                    onClick={() => { setSelectedSlot(s); setNextSlotsAfterConflict([]); }}
                    className="px-3 py-1 rounded bg-blue-600 text-white text-sm"
                  >
                    {fmtTime(s.start_at)}
                  </button>
                ))}
              </div>
            </div>
          )}

          {selectedSlot && (
            <div className={`rounded-lg border p-4 ${getLightBorder(isDarkMode)} ${getCardBackground(isDarkMode)}`}>
              <h2 className={`text-lg font-semibold mb-3 ${getPrimaryText(isDarkMode)}`}>Your details</h2>
              <div className={`mb-3 text-sm ${getSecondaryText(isDarkMode)}`}>
                {pickLabel} · {fmtDate(date)} · {fmtTime(selectedSlot.start_at)} – {fmtTime(selectedSlot.end_at)}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                <label className={`text-xs ${getMutedText(isDarkMode)}`}>
                  Email *
                  <input type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} onBlur={onEmailBlur}
                    className={`w-full mt-1 ${getInputStyles(isDarkMode)} px-2 py-2 rounded`} required />
                </label>
                <label className={`text-xs ${getMutedText(isDarkMode)}`}>
                  Name
                  <input value={contactName} onChange={(e) => setContactName(e.target.value)}
                    className={`w-full mt-1 ${getInputStyles(isDarkMode)} px-2 py-2 rounded`} />
                </label>
                <label className={`text-xs ${getMutedText(isDarkMode)}`}>
                  Phone
                  <input value={contactPhone} onChange={(e) => setContactPhone(e.target.value)}
                    className={`w-full mt-1 ${getInputStyles(isDarkMode)} px-2 py-2 rounded`} />
                </label>
                <label className={`text-xs ${getMutedText(isDarkMode)}`}>
                  Party size
                  <input type="number" min={1} value={partySize} onChange={(e) => setPartySize(parseInt(e.target.value, 10) || 1)}
                    className={`w-full mt-1 ${getInputStyles(isDarkMode)} px-2 py-2 rounded`} />
                </label>
              </div>

              {passwordPromptOpen && !memberFound && (
                <div className={`mb-3 p-3 rounded-md border ${isDarkMode ? "border-blue-700 bg-blue-950/20" : "border-blue-200 bg-blue-50"}`}>
                  <p className={`text-sm mb-2 ${isDarkMode ? "text-gray-100" : "text-gray-800"}`}>
                    We found an existing account for this email. Enter your password to use saved details (and saved cards), or skip.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <input
                      aria-label="Password"
                      type="password" value={verifyPassword} onChange={(e) => setVerifyPassword(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onVerifyPassword(); } }}
                      placeholder="Password" autoComplete="current-password"
                      className={`flex-1 ${getInputStyles(isDarkMode)} px-3 py-2 rounded text-sm`}
                    />
                    <button type="button" onClick={onVerifyPassword} disabled={verifyLoading || !verifyPassword}
                      className="px-3 py-2 rounded text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50">
                      {verifyLoading ? "Verifying…" : "Verify"}
                    </button>
                    <button type="button" onClick={skipPasswordPrompt}
                      className={`px-3 py-2 rounded text-sm ${isDarkMode ? "text-gray-300 hover:bg-gray-800" : "text-gray-700 hover:bg-gray-200"}`}>
                      Skip
                    </button>
                  </div>
                  {verifyError && <p className="mt-2 text-xs text-red-600">{verifyError}</p>}
                </div>
              )}

              <label className={`block text-xs mb-3 ${getMutedText(isDarkMode)}`}>
                Notes (optional)
                <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                  className={`w-full mt-1 ${getInputStyles(isDarkMode)} px-2 py-2 rounded`} rows={3} />
              </label>

              {requiredWaivers.length > 0 && (
                <div className={`mb-4 p-3 rounded border ${getLightBorder(isDarkMode)}`}>
                  <div className={`text-sm font-semibold mb-2 ${getPrimaryText(isDarkMode)}`}>Required waivers</div>
                  {requiredWaivers.map((w) => (
                    <div key={w.id} className="mb-3 last:mb-0">
                      <div className={`text-sm font-medium ${getPrimaryText(isDarkMode)}`}>{w.title}</div>
                      <div className={`text-xs mt-1 mb-2 max-h-32 overflow-y-auto p-2 rounded border ${getLightBorder(isDarkMode)} ${getMutedText(isDarkMode)}`}>
                        {w.content}
                      </div>
                      <label className={`inline-flex items-center text-xs ${getSecondaryText(isDarkMode)}`}>
                        <input type="checkbox" checked={acceptedWaivers.has(w.id)}
                          onChange={(e) => {
                            setAcceptedWaivers((prev) => {
                              const next = new Set(prev);
                              if (e.target.checked) next.add(w.id); else next.delete(w.id);
                              return next;
                            });
                          }}
                          className="mr-2" />
                        I have read and agree to the {w.title}.
                      </label>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex gap-2 justify-end">
                <button onClick={() => setSelectedSlot(null)} className={`px-4 py-2 rounded border text-sm ${getSecondaryText(isDarkMode)}`}>Back</button>
                <button onClick={handleBook} disabled={submitting || !allWaiversAccepted}
                  title={!allWaiversAccepted ? "Accept all required waivers to continue" : undefined}
                  className="px-4 py-2 rounded bg-blue-600 text-white text-sm disabled:opacity-60 hover:bg-blue-700">
                  {submitting ? "Booking…" : "Book"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ============================
  // GALLERY VIEW
  // ============================
  return (
    <div className={`min-h-screen ${getPageBackground(isDarkMode)} p-4 sm:p-8`}>
      <div className="max-w-5xl mx-auto">
        <h1 className={`text-3xl font-bold mb-2 ${getPrimaryText(isDarkMode)}`}>Book a facility</h1>
        <p className={`mb-6 ${getSecondaryText(isDarkMode)}`}>
          Pick a space or package below to see available times.
        </p>

        {loadingGallery ? (
          <p className={getMutedText(isDarkMode)}>Loading…</p>
        ) : resources.length === 0 && services.length === 0 ? (
          <div className={`rounded-lg border p-6 ${getLightBorder(isDarkMode)} ${getCardBackground(isDarkMode)} text-center`}>
            <p className={getMutedText(isDarkMode)}>This club doesn't currently offer public bookings.</p>
          </div>
        ) : (
          <>
            {services.length > 0 && (
              <section className="mb-8">
                <h2 className={`text-xl font-semibold mb-3 ${getPrimaryText(isDarkMode)}`}>Packages</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {services.map((s) => {
                    const cover = s.image_urls && s.image_urls.length > 0 ? s.image_urls[0] : null;
                    return (
                      <button key={s.id}
                        onClick={() => { setPick({ kind: "service", data: s }); setView("details"); }}
                        className={`text-left rounded-lg border overflow-hidden ${getLightBorder(isDarkMode)} ${getCardBackground(isDarkMode)} hover:border-blue-500 transition-colors`}>
                        {cover && (
                          <img src={cover.url} alt={cover.alt || s.name}
                            className="w-full h-32 object-cover" loading="lazy" />
                        )}
                        <div className="p-4">
                          <div className={`font-semibold ${getPrimaryText(isDarkMode)}`}>{s.name}</div>
                          <div className={`text-xs ${getMutedText(isDarkMode)} mb-2`}>{s.duration_minutes} min</div>
                          {s.description && <p className={`text-sm ${getSecondaryText(isDarkMode)} line-clamp-3`}>{s.description}</p>}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </section>
            )}

            {resources.length > 0 && (
              <section>
                <h2 className={`text-xl font-semibold mb-3 ${getPrimaryText(isDarkMode)}`}>Spaces</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {resources.map((r) => {
                    // Resource photos win; fall back to the type's
                    // photos so admins can set a default once per type
                    // (e.g. one stock court image) and only override
                    // per-resource when a specific shot exists.
                    const typeImages = (r as any).facility_resource_types?.image_urls;
                    const cover =
                      r.image_urls && r.image_urls.length > 0 ? r.image_urls[0]
                      : (Array.isArray(typeImages) && typeImages.length > 0 ? typeImages[0] : null);
                    return (
                      <button key={r.id}
                        onClick={() => { setPick({ kind: "resource", data: r }); setView("details"); }}
                        className={`text-left rounded-lg border overflow-hidden ${getLightBorder(isDarkMode)} ${getCardBackground(isDarkMode)} hover:border-blue-500 transition-colors`}>
                        {cover && (
                          <img src={cover.url} alt={cover.alt || r.name}
                            className="w-full h-32 object-cover" loading="lazy" />
                        )}
                        <div className="p-4">
                          <div className={`font-semibold ${getPrimaryText(isDarkMode)}`}>{r.name}</div>
                          <div className={`text-xs ${getMutedText(isDarkMode)} mb-2`}>
                            {r.facility_resource_types?.display_name}
                            {r.capacity > 1 && ` · capacity ${r.capacity}`}
                          </div>
                          {r.description && <p className={`text-sm ${getSecondaryText(isDarkMode)} line-clamp-3`}>{r.description}</p>}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PublicFacilityPage;
